import { canPreUploadGenerationInputFiles } from './uploadInputMedia';

interface PreUploadApplicability {
  expectedSlots: number;
  canShareAcrossRequests: boolean;
  requiredFiles: File[];
}

export interface PreparedFanOutInputs {
  requiredFiles: File[];
  preUploadedUrls?: string[];
  preUploadError?: unknown;
}

type UploadInputs = (files: File[]) => Promise<string[]>;
export const GENERATION_INPUT_PRE_UPLOAD_TIMEOUT_MS = 60_000;

export async function refreshGenerationToken(
  getFreshToken: () => Promise<string | null>,
  currentToken: string,
): Promise<string> {
  try {
    return await getFreshToken() || currentToken;
  } catch {
    return currentToken;
  }
}

export function shouldPreUploadInputs({
  expectedSlots,
  canShareAcrossRequests,
  requiredFiles,
}: PreUploadApplicability): boolean {
  return expectedSlots > 1
    && canShareAcrossRequests
    && canPreUploadGenerationInputFiles(requiredFiles);
}

export async function uploadGenerationInputs(
  files: File[],
  token: string,
  organizationId: string | number,
): Promise<string[]> {
  const formData = new FormData();
  formData.set('fileCount', String(files.length));
  files.forEach((file, index) => formData.set(`file_${index}`, file));

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GENERATION_INPUT_PRE_UPLOAD_TIMEOUT_MS);
  try {
    const response = await fetch('/api/workflow/upload-inputs', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Organization-Id': String(organizationId),
      },
      body: formData,
      signal: controller.signal,
    });
    const result = await response.json() as { uploadedUrls?: unknown; error?: unknown };

    if (!response.ok || !Array.isArray(result.uploadedUrls)) {
      throw new Error(
        typeof result.error === 'string' ? result.error : 'Failed to pre-upload generation inputs'
      );
    }

    const urls = result.uploadedUrls;
    const hasValidUrls = urls.every((url: unknown) => {
      if (typeof url !== 'string' || url.trim().length === 0) return false;
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    });
    if (!hasValidUrls || urls.length !== files.length) {
      throw new Error('Invalid pre-upload response');
    }

    return urls as string[];
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function prepareFanOutInputs(
  applicability: PreUploadApplicability,
  uploadInputs: UploadInputs,
): Promise<PreparedFanOutInputs> {
  if (!shouldPreUploadInputs(applicability)) {
    return { requiredFiles: applicability.requiredFiles };
  }

  try {
    const preUploadedUrls = await uploadInputs(applicability.requiredFiles);
    return { requiredFiles: [], preUploadedUrls };
  } catch (preUploadError) {
    return { requiredFiles: applicability.requiredFiles, preUploadError };
  }
}
